import { useState, useEffect, useCallback, useRef } from 'react';
import { Save, ExternalLink, BarChart3, Eye, MousePointer, Globe, Users, Monitor, Smartphone, Tablet, RefreshCw, Zap, Clock, Download, X, TrendingUp, Activity } from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart, Area,
  BarChart as RBarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Brush,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;
const PERIODS = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
  { label: '1Y', days: 365 },
];

/* ---- Animated counter ---- */
function AnimNum({ value, duration = 800 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const num = typeof value === 'number' ? value : parseInt(value?.toString().replace(/,/g, ''), 10) || 0;
    const start = display;
    const diff = num - start;
    if (diff === 0) return;
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + diff * ease));
      if (p < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(ref.current);
  }, [value]); // eslint-disable-line
  return <>{display.toLocaleString()}</>;
}

/* ---- Helpers ---- */
const fmtNum = (v) => {
  if (v === null || v === undefined) return '0';
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return Math.round(v).toLocaleString();
};
const fmtDate = (raw) => {
  if (!raw || raw.length !== 8) return raw;
  const m = parseInt(raw.slice(4, 6), 10);
  const d = parseInt(raw.slice(6, 8), 10);
  return `${d}/${m}`;
};
const fmtPath = (p) => {
  if (!p) return p;
  if (p === '/' || p === '') return 'Home';
  return p;
};

/* ---- Custom Tooltip (mobile-friendly, dark) ---- */
function ChartTooltip({ active, payload, label, labelFormatter, valueLabel }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-[#0D0D0D]/95 backdrop-blur-md px-3 py-2 shadow-2xl pointer-events-none">
      {label !== undefined && (
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
          {labelFormatter ? labelFormatter(label) : label}
        </div>
      )}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.fill }} />
          <span className="text-slate-300 capitalize">{valueLabel || p.name}</span>
          <span className="ml-auto font-mono font-semibold text-white">{fmtNum(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

/* ---- Trend Chart (Users + Views, mobile-first, drag-to-zoom) ---- */
function TrendChart({ data, height = 240 }) {
  const showBrush = data?.length > 12;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <defs>
          <linearGradient id="grdUsers" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#00E5FF" stopOpacity={0.45} />
            <stop offset="100%" stopColor="#00E5FF" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="grdViews" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.35} />
            <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={fmtDate}
          stroke="#475569"
          tick={{ fill: '#64748B', fontSize: 10 }}
          axisLine={false}
          tickLine={false}
          minTickGap={20}
        />
        <YAxis
          tickFormatter={fmtNum}
          stroke="#475569"
          tick={{ fill: '#64748B', fontSize: 10 }}
          axisLine={false}
          tickLine={false}
          width={36}
        />
        <Tooltip
          content={<ChartTooltip labelFormatter={fmtDate} />}
          cursor={{ stroke: '#00E5FF', strokeOpacity: 0.3, strokeWidth: 1 }}
          wrapperStyle={{ outline: 'none' }}
        />
        <Area
          type="monotone"
          dataKey="users"
          name="Users"
          stroke="#00E5FF"
          strokeWidth={2.25}
          fill="url(#grdUsers)"
          activeDot={{ r: 6, fill: '#00E5FF', stroke: '#0A0A0A', strokeWidth: 2 }}
          isAnimationActive
          animationDuration={700}
        />
        <Area
          type="monotone"
          dataKey="views"
          name="Views"
          stroke="#3B82F6"
          strokeWidth={2}
          fill="url(#grdViews)"
          activeDot={{ r: 5, fill: '#3B82F6', stroke: '#0A0A0A', strokeWidth: 2 }}
          isAnimationActive
          animationDuration={900}
        />
        {showBrush && (
          <Brush
            dataKey="date"
            height={22}
            travellerWidth={10}
            stroke="rgba(0,229,255,0.45)"
            fill="rgba(0,229,255,0.05)"
            tickFormatter={fmtDate}
          />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
}

/* ---- Horizontal Bar Chart (categorical, axis + tooltip, mobile-friendly) ---- */
function CategoryBars({ items, valueLabel = 'Value', colorFn, height }) {
  if (!items?.length) return null;
  const data = items.map((i) => ({ label: i.label, value: i.value }));
  const h = height ?? Math.max(140, data.length * 36 + 28);
  return (
    <ResponsiveContainer width="100%" height={h}>
      <RBarChart data={data} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} />
        <XAxis
          type="number"
          tickFormatter={fmtNum}
          stroke="#475569"
          tick={{ fill: '#64748B', fontSize: 10 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey="label"
          stroke="#475569"
          tick={{ fill: '#94A3B8', fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          width={88}
          tickFormatter={(v) => (typeof v === 'string' && v.length > 14 ? `${v.slice(0, 13)}…` : v)}
        />
        <Tooltip
          content={<ChartTooltip valueLabel={valueLabel} />}
          cursor={{ fill: 'rgba(255,255,255,0.04)' }}
          wrapperStyle={{ outline: 'none' }}
        />
        <Bar dataKey="value" name={valueLabel} radius={[0, 6, 6, 0]} isAnimationActive animationDuration={700}>
          {data.map((_, i) => (
            <Cell key={i} fill={colorFn(i)} />
          ))}
        </Bar>
      </RBarChart>
    </ResponsiveContainer>
  );
}

/* ---- Donut (PieChart) ---- */
function DeviceDonut({ segments }) {
  if (!segments?.length) return null;
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  const DEVICE_ICONS = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };
  return (
    <div className="flex items-center gap-4">
      <div className="relative w-32 h-32 flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={segments}
              dataKey="value"
              nameKey="label"
              innerRadius="62%"
              outerRadius="92%"
              paddingAngle={2}
              stroke="none"
              isAnimationActive
              animationDuration={800}
            >
              {segments.map((s, i) => (
                <Cell key={i} fill={s.color} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip valueLabel="Sessions" />} wrapperStyle={{ outline: 'none' }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="font-['Outfit'] text-base font-black text-white">{total.toLocaleString()}</span>
        </div>
      </div>
      <div className="space-y-2 flex-1 min-w-0">
        {segments.map((seg) => {
          const Icon = DEVICE_ICONS[seg.label.toLowerCase()] || Monitor;
          const pct = Math.round((seg.value / total) * 100);
          return (
            <div key={seg.label} className="flex items-center gap-2">
              <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: seg.color }} />
              <span className="text-[11px] text-slate-300 capitalize flex-1 truncate">{seg.label}</span>
              <span className="text-[11px] font-semibold text-white">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---- Modal ---- */
function DetailModal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0D0D0D] border border-white/10 rounded-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto animate-[slideUp_0.3s_ease]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/5 sticky top-0 bg-[#0D0D0D] z-10">
          <h3 className="font-['Outfit'] text-lg font-bold text-white">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

/* ---- Stat Card ---- */
function StatCard({ icon: Icon, label, value, suffix, gradient, sub, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`relative rounded-2xl p-5 overflow-hidden group transition-all duration-300 border border-white/[0.06] hover:border-white/15 ${onClick ? 'cursor-pointer hover:scale-[1.02]' : ''}`}
      style={{ background: 'linear-gradient(135deg, rgba(17,17,17,1) 0%, rgba(20,20,25,1) 100%)' }}
    >
      <div className={`absolute -top-8 -right-8 w-32 h-32 rounded-full blur-[50px] opacity-30 group-hover:opacity-50 transition-opacity duration-500 ${gradient}`} />
      <div className="relative">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${gradient}/10`} style={{ background: `linear-gradient(135deg, ${gradient === 'bg-cyan-500' ? 'rgba(0,229,255,0.15)' : gradient === 'bg-blue-500' ? 'rgba(59,130,246,0.15)' : gradient === 'bg-violet-500' ? 'rgba(139,92,246,0.15)' : 'rgba(16,185,129,0.15)'}, transparent)` }}>
          <Icon className="w-5 h-5" style={{ color: gradient === 'bg-cyan-500' ? '#00E5FF' : gradient === 'bg-blue-500' ? '#3B82F6' : gradient === 'bg-violet-500' ? '#8B5CF6' : '#10B981' }} />
        </div>
        <p className="font-['Outfit'] text-3xl font-black text-white tracking-tight">
          <AnimNum value={typeof value === 'string' ? parseInt(value?.replace(/,/g, ''), 10) || 0 : value || 0} />
          {suffix && <span className="text-sm font-normal text-slate-500 ml-1">{suffix}</span>}
        </p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
        {sub && <p className="text-[10px] text-slate-600 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

/* ---- Export function ---- */
function exportCSV(dashboard, days) {
  if (!dashboard) return;
  const ov = dashboard.overview || {};
  let csv = `UI DESIGNS Analytics Export — Last ${days} Days\n\n`;
  csv += `Metric,Value\n`;
  csv += `Total Visitors,${ov.total_users || 0}\n`;
  csv += `Page Views,${ov.page_views || 0}\n`;
  csv += `Sessions,${ov.sessions || 0}\n`;
  csv += `Avg Duration (sec),${ov.avg_session_duration || 0}\n`;
  csv += `Bounce Rate,${ov.bounce_rate || 0}%\n`;
  csv += `New Users,${ov.new_users || 0}\n\n`;

  if (dashboard.top_pages?.length) {
    csv += `Top Pages\nPath,Views\n`;
    dashboard.top_pages.forEach(p => { csv += `"${p.path}",${p.views}\n`; });
    csv += '\n';
  }
  if (dashboard.sources?.length) {
    csv += `Traffic Sources\nChannel,Sessions\n`;
    dashboard.sources.forEach(s => { csv += `${s.channel},${s.sessions}\n`; });
    csv += '\n';
  }
  if (dashboard.countries?.length) {
    csv += `Top Countries\nCountry,Users\n`;
    dashboard.countries.forEach(c => { csv += `${c.country},${c.users}\n`; });
    csv += '\n';
  }
  if (dashboard.daily?.length) {
    csv += `Daily Breakdown\nDate,Users,Views\n`;
    dashboard.daily.forEach(d => { csv += `${d.date},${d.users},${d.views}\n`; });
  }

  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `uidesigns-analytics-${days}d-${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ==== MAIN ==== */
export default function AnalyticsManager() {
  const [ga4Id, setGa4Id] = useState('');
  const [clarityId, setClarityId] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashLoading, setDashLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [period, setPeriod] = useState(30);
  const [modal, setModal] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/analytics/settings`);
      const data = await res.json();
      setGa4Id(data.ga4_id || '');
      setClarityId(data.clarity_id || '');
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchDashboard = useCallback(async (days) => {
    setDashLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API}/api/admin/analytics/dashboard?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.error) setDashboard(data);
    } catch (err) { console.error(err); }
    finally { setDashLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { fetchDashboard(period); }, [period, fetchDashboard]);

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API}/api/admin/analytics/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ga4_id: ga4Id, clarity_id: clarityId }),
      });
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const ov = dashboard?.overview || {};
  const deviceColors = { desktop: '#3B82F6', mobile: '#00E5FF', tablet: '#8B5CF6' };
  const sourceColors = ['#00E5FF', '#3B82F6', '#8B5CF6', '#10B981', '#F59E0B', '#EF4444'];

  if (loading) {
    return (
      <div data-testid="analytics-manager" className="space-y-4">
        {[1,2].map(i => <div key={i} className="h-40 bg-white/[0.03] rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  return (
    <div data-testid="analytics-manager" className="animate-[fadeIn_0.4s_ease]">
      {/* Keyframe styles */}
      <style>{`
        @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(20px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes drawLine { from { stroke-dasharray: 0 9999; } to { stroke-dasharray: 9999 0; } }
        .recharts-wrapper, .recharts-surface, .recharts-wrapper *, .recharts-surface * { outline: none !important; }
        .recharts-wrapper:focus, .recharts-surface:focus, .recharts-wrapper *:focus, .recharts-surface *:focus { outline: none !important; }
        .recharts-wrapper:focus-visible, .recharts-surface:focus-visible { outline: none !important; box-shadow: none !important; }
      `}</style>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-['Outfit'] text-xl font-bold text-white">Analytics</h2>
          <p className="text-sm text-slate-500 mt-0.5">Real-time data from Google Analytics.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exportCSV(dashboard, period)} data-testid="export-csv-btn" className="w-9 h-9 rounded-xl border border-white/10 flex items-center justify-center text-slate-500 hover:text-emerald-400 hover:border-emerald-500/30 transition-all" title="Export CSV">
            <Download className="w-4 h-4" />
          </button>
          <button onClick={() => fetchDashboard(period)} disabled={dashLoading} className="w-9 h-9 rounded-xl border border-white/10 flex items-center justify-center text-slate-500 hover:text-[#00E5FF] hover:border-[#00E5FF]/30 transition-all">
            <RefreshCw className={`w-4 h-4 ${dashLoading ? 'animate-spin' : ''}`} />
          </button>
          {saved && <span className="text-xs text-emerald-400">Saved</span>}
          <button data-testid="analytics-save-btn" onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-4 py-2 rounded-xl text-sm font-semibold hover:bg-white transition-colors disabled:opacity-50">
            <Save className="w-3.5 h-3.5" />
            {saving ? '...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Period switcher + Realtime */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="inline-flex bg-white/[0.04] border border-white/[0.06] rounded-xl p-1">
          {PERIODS.map(p => (
            <button
              key={p.days}
              data-testid={`period-${p.days}`}
              onClick={() => setPeriod(p.days)}
              className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all duration-300 ${period === p.days ? 'bg-[#00E5FF] text-[#0A0A0A] shadow-[0_0_20px_rgba(0,229,255,0.2)]' : 'text-slate-500 hover:text-white'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-2.5">
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_10px_rgba(52,211,153,0.5)]" />
          <span className="font-['Outfit'] text-xl font-black text-white"><AnimNum value={dashboard?.realtime_users ?? 0} /></span>
          <span className="text-xs text-slate-500">live now</span>
          {dashLoading && <RefreshCw className="w-3 h-3 text-slate-600 animate-spin ml-1" />}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" key={period}>
        <StatCard icon={Users} label="Visitors" value={ov.total_users || 0} gradient="bg-cyan-500" sub={`Last ${period} days`} />
        <StatCard icon={Eye} label="Page Views" value={ov.page_views || 0} gradient="bg-blue-500" sub={`${ov.new_users || 0} new users`} onClick={() => setModal('pages')} />
        <StatCard icon={Zap} label="Sessions" value={ov.sessions || 0} gradient="bg-violet-500" sub={`Last ${period} days`} onClick={() => setModal('sources')} />
        <StatCard icon={Clock} label="Avg Duration" value={ov.avg_session_duration ? Math.floor(ov.avg_session_duration) : 0} suffix="s" gradient="bg-emerald-500" sub={`${ov.bounce_rate || 0}% bounce`} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-6">
        <div className="lg:col-span-2 bg-[#111111]/80 border border-white/[0.06] rounded-2xl p-4 sm:p-5 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-['Outfit'] text-sm font-bold text-white">Traffic Trend</h3>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5 text-[10px] text-[#00E5FF]"><span className="w-2 h-2 rounded-full bg-[#00E5FF]" />Users</span>
              <span className="flex items-center gap-1.5 text-[10px] text-blue-400"><span className="w-2 h-2 rounded-full bg-blue-400" />Views</span>
            </div>
          </div>
          {dashboard?.daily?.length > 0 ? (
            <div key={period} className="animate-[fadeIn_0.5s_ease] -mx-1 sm:mx-0 touch-pan-y">
              <TrendChart data={dashboard.daily} height={260} />
              <p className="text-[10px] text-slate-600 mt-2 sm:hidden text-center">Tap chart for details · drag the strip below to zoom</p>
            </div>
          ) : (
            <div className="h-32 flex items-center justify-center">
              {dashLoading ? <RefreshCw className="w-5 h-5 text-slate-600 animate-spin" /> : <span className="text-xs text-slate-600">No data yet</span>}
            </div>
          )}
        </div>

        <div className="bg-[#111111]/80 border border-white/[0.06] rounded-2xl p-4 sm:p-5 backdrop-blur-sm cursor-pointer hover:border-white/10 transition-all" onClick={() => setModal('devices')}>
          <h3 className="font-['Outfit'] text-sm font-bold text-white mb-4">Devices</h3>
          {dashboard?.devices?.length > 0 ? (
            <div key={period} className="animate-[fadeIn_0.5s_ease]">
              <DeviceDonut segments={dashboard.devices.map(d => ({ label: d.device, value: d.sessions, color: deviceColors[d.device] || '#64748B' }))} />
            </div>
          ) : (
            <div className="h-28 flex items-center justify-center">
              {dashLoading ? <RefreshCw className="w-4 h-4 text-slate-600 animate-spin" /> : <span className="text-xs text-slate-600">No data yet</span>}
            </div>
          )}
        </div>
      </div>

      {/* Bottom panels */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-[#111111]/80 border border-white/[0.06] rounded-2xl p-4 sm:p-5 cursor-pointer hover:border-white/10 transition-all" onClick={() => setModal('pages')}>
          <h3 className="font-['Outfit'] text-sm font-bold text-white mb-3">Top Pages</h3>
          {dashboard?.top_pages?.length > 0 ? (
            <CategoryBars
              items={dashboard.top_pages.slice(0, 5).map(p => ({ label: fmtPath(p.path), value: p.views }))}
              valueLabel="Views"
              colorFn={(i) => i === 0 ? '#00E5FF' : `rgba(0,229,255,${0.85 - i * 0.13})`}
            />
          ) : <div className="h-20 flex items-center justify-center">{dashLoading ? <RefreshCw className="w-4 h-4 text-slate-600 animate-spin" /> : <span className="text-xs text-slate-600">No data</span>}</div>}
        </div>

        <div className="bg-[#111111]/80 border border-white/[0.06] rounded-2xl p-4 sm:p-5 cursor-pointer hover:border-white/10 transition-all" onClick={() => setModal('sources')}>
          <h3 className="font-['Outfit'] text-sm font-bold text-white mb-3">Traffic Sources</h3>
          {dashboard?.sources?.length > 0 ? (
            <CategoryBars
              items={dashboard.sources.slice(0, 5).map(s => ({ label: s.channel, value: s.sessions }))}
              valueLabel="Sessions"
              colorFn={(i) => sourceColors[i % sourceColors.length]}
            />
          ) : <div className="h-20 flex items-center justify-center">{dashLoading ? <RefreshCw className="w-4 h-4 text-slate-600 animate-spin" /> : <span className="text-xs text-slate-600">No data</span>}</div>}
        </div>

        <div className="bg-[#111111]/80 border border-white/[0.06] rounded-2xl p-4 sm:p-5 cursor-pointer hover:border-white/10 transition-all" onClick={() => setModal('countries')}>
          <h3 className="font-['Outfit'] text-sm font-bold text-white mb-3">Top Countries</h3>
          {dashboard?.countries?.length > 0 ? (
            <CategoryBars
              items={dashboard.countries.slice(0, 5).map(c => ({ label: c.country, value: c.users }))}
              valueLabel="Users"
              colorFn={(i) => i === 0 ? '#8B5CF6' : `rgba(139,92,246,${0.85 - i * 0.13})`}
            />
          ) : <div className="h-20 flex items-center justify-center">{dashLoading ? <RefreshCw className="w-4 h-4 text-slate-600 animate-spin" /> : <span className="text-xs text-slate-600">No data</span>}</div>}
        </div>
      </div>

      {/* Config cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="relative bg-[#111111]/80 border border-white/[0.06] rounded-2xl p-5 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent pointer-events-none" />
          <div className="relative flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center"><BarChart3 className="w-4 h-4 text-blue-400" /></div>
            <span className="font-['Outfit'] text-sm font-bold text-white">Google Analytics</span>
            <div className={`ml-auto w-2 h-2 rounded-full ${ga4Id ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.4)]' : 'bg-slate-600'}`} />
          </div>
          <input data-testid="ga4-id-input" value={ga4Id} onChange={(e) => setGa4Id(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-blue-500/50 mb-2" placeholder="G-XXXXXXXXXX" />
          <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] text-blue-400 hover:text-blue-300"><ExternalLink className="w-3 h-3" /> GA4 Dashboard</a>
        </div>
        <div className="relative bg-[#111111]/80 border border-white/[0.06] rounded-2xl p-5 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-transparent pointer-events-none" />
          <div className="relative flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center"><MousePointer className="w-4 h-4 text-violet-400" /></div>
            <span className="font-['Outfit'] text-sm font-bold text-white">Microsoft Clarity</span>
            <div className={`ml-auto w-2 h-2 rounded-full ${clarityId ? 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.4)]' : 'bg-slate-600'}`} />
          </div>
          <input data-testid="clarity-id-input" value={clarityId} onChange={(e) => setClarityId(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-violet-500/50 mb-2" placeholder="abc123xyz" />
          <a href={`https://clarity.microsoft.com/projects/view/${clarityId}/dashboard`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] text-violet-400 hover:text-violet-300"><ExternalLink className="w-3 h-3" /> Clarity Dashboard</a>
        </div>
      </div>

      {/* ---- Modals ---- */}
      {modal === 'pages' && dashboard?.top_pages && (
        <DetailModal title="All Top Pages" onClose={() => setModal(null)}>
          <CategoryBars
            items={dashboard.top_pages.map(p => ({ label: fmtPath(p.path), value: p.views }))}
            valueLabel="Views"
            colorFn={(i) => `rgba(0,229,255,${Math.max(0.25, 1 - i * 0.07)})`}
          />
        </DetailModal>
      )}
      {modal === 'sources' && dashboard?.sources && (
        <DetailModal title="All Traffic Sources" onClose={() => setModal(null)}>
          <CategoryBars
            items={dashboard.sources.map(s => ({ label: s.channel, value: s.sessions }))}
            valueLabel="Sessions"
            colorFn={(i) => sourceColors[i % sourceColors.length]}
          />
        </DetailModal>
      )}
      {modal === 'countries' && dashboard?.countries && (
        <DetailModal title="All Countries" onClose={() => setModal(null)}>
          <CategoryBars
            items={dashboard.countries.map(c => ({ label: c.country, value: c.users }))}
            valueLabel="Users"
            colorFn={(i) => `rgba(139,92,246,${Math.max(0.25, 1 - i * 0.08)})`}
          />
        </DetailModal>
      )}
      {modal === 'devices' && dashboard?.devices && (
        <DetailModal title="Device Breakdown" onClose={() => setModal(null)}>
          <DeviceDonut segments={dashboard.devices.map(d => ({ label: d.device, value: d.sessions, color: deviceColors[d.device] || '#64748B' }))} />
          <div className="mt-4 space-y-2">
            {dashboard.devices.map(d => (
              <div key={d.device} className="flex items-center justify-between text-sm">
                <span className="text-slate-400 capitalize">{d.device}</span>
                <span className="text-white font-semibold">{d.sessions.toLocaleString()} sessions</span>
              </div>
            ))}
          </div>
        </DetailModal>
      )}
    </div>
  );
}
